// This is ScreenerFilter.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  FaChevronDown,
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp
} from 'react-icons/fa';
import './ScreenerFilter.css';

import { METRIC_FIELDS } from "../constants/metricFields";


// const ALL_METRICS = [
//   { label: "Current Ratio", key: "currentRatio" },
//   { label: "Quick Ratio", key: "quickRatio" },
//   { label: "Gross Profit Margin", key: "grossProfitMargin" },
//   { label: "Operating Profit Margin", key: "operatingProfitMargin" },
//   { label: "Net Profit Margin", key: "netProfitMargin" },
//   { label: "Return on Assets", key: "returnOnAssets" },
//   { label: "Return on Equity", key: "returnOnEquity" },
//   { label: "Debt to Equity Ratio", key: "debtEquityRatio" },
//   { label: "Free Cash Flow per Share", key: "freeCashFlowPerShare" },
//   { label: "FCF / OCF Ratio", key: "freeCashFlowOperatingCashFlowRatio" },
//   { label: "Price to Free Cash Flow", key: "priceToFreeCashFlowsRatio" },
//   { label: "Price to Earnings Ratio", key: "priceEarningsRatio" },
//   { label: "Price to Sales Ratio", key: "priceToSalesRatio" }
// ];

const ALL_METRICS = METRIC_FIELDS
  .map(m => ({
    label: m.label,
    key: m.value
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

export const filterMetricsByLabel = (metrics, metricRanges, searchTerm) => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  return metrics
    .filter(metric => metricRanges[metric.key])
    .filter(metric =>
      !normalizedSearchTerm || metric.label.toLowerCase().includes(normalizedSearchTerm)
    );
};

const buildActiveFilters = (filtersByMetric) =>
  Object.entries(filtersByMetric)
    .filter(([_, filter]) => filter.enabled)
    .map(([key, filter]) => ({
      metric: key,
      min: filter.min,
      max: filter.max
    }));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const useDebouncedValue = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [value, delay]);

  return debouncedValue;
};

const queueFocus = (callback) => {
  if (typeof window !== 'undefined' && window.requestAnimationFrame) {
    window.requestAnimationFrame(callback);
    return;
  }

  callback();
};

const ScreenerFilter = ({
  onFilterChange,
  activeFilters,
  metricsData,
  isCollapsed: controlledIsCollapsed,
  onCollapsedChange,
  collapseDirection = 'vertical'
}) => {
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const [filters, setFilters] = useState({});
  const [metricSearchTerm, setMetricSearchTerm] = useState('');
  const [activeMetricIndex, setActiveMetricIndex] = useState(0);
  const activeFiltersRef = useRef(activeFilters);
  const hasInitializedFilters = useRef(false);
  const metricCheckboxRefs = useRef({});
  const skipNextFilterNotification = useRef(false);
  const debouncedMetricSearchTerm = useDebouncedValue(metricSearchTerm, 200);
  const isCollapseControlled = typeof controlledIsCollapsed === 'boolean';
  const isCollapsed = isCollapseControlled
    ? controlledIsCollapsed
    : internalIsCollapsed;

  const handleCollapseToggle = useCallback(() => {
    const nextIsCollapsed = !isCollapsed;

    if (!isCollapseControlled) {
      setInternalIsCollapsed(nextIsCollapsed);
    }

    onCollapsedChange?.(nextIsCollapsed);
  }, [isCollapseControlled, isCollapsed, onCollapsedChange]);

  // Calculate min/max ranges for all metrics from metricsData
  const metricRanges = useMemo(() => {
    if (!metricsData || Object.keys(metricsData).length === 0) return {};
    
    const ranges = {};
    
    ALL_METRICS.forEach(metric => {
      const values = Object.values(metricsData)
        // .map(stock => stock[metric.key])
                .filter(stock => stock !== null && stock !== undefined)
        .map(stock => stock[metric.key])
        .filter(val => val !== null && val !== undefined && !isNaN(val));
      
      if (values.length > 0) {
        const min = Math.min(...values);
        const max = Math.max(...values);
        const padding = (max - min) * 0.05; // 5% padding
        
        ranges[metric.key] = {
          min: Math.floor((min - padding) * 100) / 100,
          max: Math.ceil((max + padding) * 100) / 100,
          step: (max - min) > 100 ? 1 : 0.01
        };
      }
    });
    
    return ranges;
  }, [metricsData]);

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  // Initialize filters when ranges are calculated without resetting on every slider drag.
  useEffect(() => {
    if (Object.keys(metricRanges).length === 0) {
      hasInitializedFilters.current = false;
      setFilters({});
      return;
    }

    const activeFilterByMetric = new Map(
      (activeFiltersRef.current || []).map((filter) => [filter.metric, filter])
    );

    hasInitializedFilters.current = true;
    skipNextFilterNotification.current = true;
    setFilters(prev => {
      const initialFilters = {};

      ALL_METRICS.forEach(metric => {
        const range = metricRanges[metric.key];

        if (range) {
          const externalFilter = activeFilterByMetric.get(metric.key);
          const currentFilter = prev[metric.key];
          const nextMin = clamp(currentFilter?.min ?? externalFilter?.min ?? range.min, range.min, range.max);
          const nextMax = clamp(currentFilter?.max ?? externalFilter?.max ?? range.max, range.min, range.max);

          initialFilters[metric.key] = {
            min: Math.min(nextMin, nextMax),
            max: Math.max(nextMin, nextMax),
            enabled: currentFilter?.enabled ?? !!externalFilter
          };
        }
      });

      return initialFilters;
    });
  }, [metricRanges]);

  useEffect(() => {
    if (!hasInitializedFilters.current) {
      return;
    }

    if (skipNextFilterNotification.current) {
      skipNextFilterNotification.current = false;
      return;
    }

    onFilterChange?.(buildActiveFilters(filters));
  }, [filters, onFilterChange]);

  // Update filters when slider changes
  const handleSliderChange = useCallback((metricKey, type, value) => {
    const numericValue = parseFloat(value);

    if (!Number.isFinite(numericValue)) {
      return;
    }

    setFilters(prev => {
      const currentFilter = prev[metricKey];
      const range = metricRanges[metricKey];

      if (!currentFilter || !range) {
        return prev;
      }

      const boundedValue = clamp(numericValue, range.min, range.max);
      const updated = {
        ...prev,
        [metricKey]: {
          ...currentFilter,
          [type]: type === 'min'
            ? Math.min(boundedValue, currentFilter.max)
            : Math.max(boundedValue, currentFilter.min),
          enabled: true
        }
      };

      return updated;
    });
  }, [metricRanges]);

  // Calculate slider fill percentage for dynamic range visualization
  const getSliderFillStyle = useCallback((metricKey) => {
    const range = metricRanges[metricKey];
    const filter = filters[metricKey];
    
    if (!range || !filter) return { display: 'none' };
    
    const totalRange = range.max - range.min;

    if (totalRange <= 0) {
      return { left: '0%', width: '100%' };
    }

    const minPercent = clamp(((filter.min - range.min) / totalRange) * 100, 0, 100);
    const maxPercent = clamp(((filter.max - range.min) / totalRange) * 100, 0, 100);
    
    return {
      left: `${Math.min(minPercent, maxPercent)}%`,
      width: `${Math.abs(maxPercent - minPercent)}%`
    };
  }, [metricRanges, filters]);

  // Toggle metric filter on/off
  const toggleMetricFilter = useCallback((metricKey) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        [metricKey]: {
          ...prev[metricKey],
          enabled: !prev[metricKey]?.enabled
        }
      };

      return updated;
    });
  }, []);

  const visibleMetrics = useMemo(
    () => filterMetricsByLabel(ALL_METRICS, metricRanges, debouncedMetricSearchTerm),
    [metricRanges, debouncedMetricSearchTerm]
  );

  useEffect(() => {
    setActiveMetricIndex(0);
  }, [debouncedMetricSearchTerm, visibleMetrics.length]);

  const focusVisibleMetric = useCallback((index) => {
    const nextMetric = visibleMetrics[index];

    if (nextMetric) {
      metricCheckboxRefs.current[nextMetric.key]?.focus();
    }
  }, [visibleMetrics]);

  const moveMetricFocus = useCallback((direction, currentIndex = activeMetricIndex) => {
    if (!visibleMetrics.length) {
      return;
    }

    const nextIndex = (currentIndex + direction + visibleMetrics.length) % visibleMetrics.length;
    setActiveMetricIndex(nextIndex);
    queueFocus(() => focusVisibleMetric(nextIndex));
  }, [activeMetricIndex, focusVisibleMetric, visibleMetrics.length]);

  const handleMetricSearchKeyDown = useCallback((event) => {
    if (!visibleMetrics.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveMetricIndex(0);
      queueFocus(() => focusVisibleMetric(0));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const lastIndex = visibleMetrics.length - 1;
      setActiveMetricIndex(lastIndex);
      queueFocus(() => focusVisibleMetric(lastIndex));
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      toggleMetricFilter(visibleMetrics[activeMetricIndex]?.key || visibleMetrics[0].key);
    }
  }, [activeMetricIndex, focusVisibleMetric, toggleMetricFilter, visibleMetrics]);

  const handleMetricResultKeyDown = useCallback((event, metricKey, index) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveMetricFocus(1, index);
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveMetricFocus(-1, index);
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      toggleMetricFilter(metricKey);
    }
  }, [moveMetricFocus, toggleMetricFilter]);

  const selectAllVisibleMetrics = useCallback(() => {
    if (!visibleMetrics.length) {
      return;
    }

    setFilters(prev => {
      const updated = { ...prev };

      visibleMetrics.forEach(metric => {
        const currentFilter = updated[metric.key];

        if (currentFilter) {
          updated[metric.key] = {
            ...currentFilter,
            enabled: true
          };
        }
      });

      return updated;
    });
  }, [visibleMetrics]);

  // Reset all filters
  const resetAllFilters = useCallback(() => {
    const resetFilters = {};
    ALL_METRICS.forEach(metric => {
      if (metricRanges[metric.key]) {
        resetFilters[metric.key] = {
          min: metricRanges[metric.key].min,
          max: metricRanges[metric.key].max,
          enabled: false
        };
      }
    });
    
    setFilters(resetFilters);
  }, [metricRanges]);

  const activeFilterCount = Object.values(filters).filter(f => f.enabled).length;
  const CollapseIcon = collapseDirection === 'horizontal'
    ? (isCollapsed ? FaChevronLeft : FaChevronRight)
    : (isCollapsed ? FaChevronDown : FaChevronUp);

  return (
    <div className="screener-filter-container">
      <div className="screener-filter-header">
        <h3>Filters</h3>
        <div className="filter-header-actions">
          {activeFilterCount > 0 && (
            <span className="active-filter-count">{activeFilterCount}</span>
          )}
          <button 
            className="collapse-btn" 
            type="button"
            onClick={handleCollapseToggle}
            aria-label={isCollapsed ? "Expand filters" : "Collapse filters"}
            aria-expanded={!isCollapsed}
          >
            <CollapseIcon />
          </button>
        </div>
      </div>

      <div className={`screener-filter-content ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="metric-search-panel">
          <label className="metric-search-label" htmlFor="screener-metric-search">
            Find metrics
          </label>
          <input
            id="screener-metric-search"
            className="metric-search-input"
            type="search"
            value={metricSearchTerm}
            onChange={(event) => setMetricSearchTerm(event.target.value)}
            onKeyDown={handleMetricSearchKeyDown}
            placeholder="Search metrics"
            aria-controls="screener-metric-list"
            aria-label="Search screener metrics"
          />
          <div className="metric-search-actions">
            <span className="metric-search-count">
              {visibleMetrics.length} {visibleMetrics.length === 1 ? 'metric' : 'metrics'}
            </span>
            <div className="metric-search-button-group">
              <button
                type="button"
                className="select-visible-metrics-btn"
                onClick={selectAllVisibleMetrics}
                disabled={!visibleMetrics.length}
              >
                Select all visible
              </button>
              {activeFilterCount > 0 && (
                <button className="reset-filters-btn" onClick={resetAllFilters}>
                  Clear all
                </button>
              )}
            </div>
          </div>
        </div>
        
        <div className="metrics-list" id="screener-metric-list">
          {!visibleMetrics.length && (
            <p className="metric-search-empty">No matching metrics</p>
          )}
          {visibleMetrics.map((metric, index) => {
            const range = metricRanges[metric.key];
            const filter = filters[metric.key];
            
            if (!range || !filter) return null;

            return (
              <div key={metric.key} className="metric-filter-item">
                <div className="metric-filter-header">
                  <label className="metric-checkbox">
                    <input
                      type="checkbox"
                      ref={(element) => {
                        if (element) {
                          metricCheckboxRefs.current[metric.key] = element;
                        } else {
                          delete metricCheckboxRefs.current[metric.key];
                        }
                      }}
                      checked={filter.enabled || false}
                      onChange={() => toggleMetricFilter(metric.key)}
                      onFocus={() => setActiveMetricIndex(index)}
                      onKeyDown={(event) => handleMetricResultKeyDown(event, metric.key, index)}
                    />
                    <span className="metric-label">{metric.label}</span>
                  </label>
                </div>
                
                {filter.enabled && (
                  <div className="slider-container">
                    <div className="filter-value-controls">
                      <label className="filter-value-field">
                        <span>Min</span>
                        <input
                          type="number"
                          min={range.min}
                          max={filter.max}
                          step={range.step}
                          value={filter.min}
                          onChange={(e) => handleSliderChange(metric.key, 'min', e.target.value)}
                          aria-label={`${metric.label} minimum value`}
                        />
                      </label>
                      <label className="filter-value-field">
                        <span>Max</span>
                        <input
                          type="number"
                          min={filter.min}
                          max={range.max}
                          step={range.step}
                          value={filter.max}
                          onChange={(e) => handleSliderChange(metric.key, 'max', e.target.value)}
                          aria-label={`${metric.label} maximum value`}
                        />
                      </label>
                    </div>
                    
                    <div className="dual-range-slider">
                      <div 
                        className="slider-fill" 
                        style={getSliderFillStyle(metric.key)}
                      />
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        step={range.step}
                        value={filter.min}
                        onChange={(e) => handleSliderChange(metric.key, 'min', e.target.value)}
                        className="range-slider range-slider-min"
                      />
                      <input
                        type="range"
                        min={range.min}
                        max={range.max}
                        step={range.step}
                        value={filter.max}
                        onChange={(e) => handleSliderChange(metric.key, 'max', e.target.value)}
                        className="range-slider range-slider-max"
                      />
                    </div>
                    
                    <div className="range-labels">
                      <span className="range-min">{range.min}</span>
                      <span className="range-max">{range.max}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ScreenerFilter;
